/**
 * Prijava preko Google/Apple (OIDC, web preusmjeravanje): konfiguracija provajdera, početak toka i
 * kratkotrajno stanje u sessionStorage (namjera po `state`, tiket za registraciju sa identitetom).
 */
import type { OidcIntent, OidcProvider } from '@sm/api';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { api, unwrap } from '@/lib/api';
import { qk } from '@/lib/queries';

export const OIDC_PROVIDERS: readonly OidcProvider[] = ['google', 'apple', 'dev'];

export function isOidcProvider(v: unknown): v is OidcProvider {
  return typeof v === 'string' && (OIDC_PROVIDERS as readonly string[]).includes(v);
}

/** Naziv provajdera za prikaz (vlastita imena se ne prevode). */
export const PROVIDER_LABEL: Record<OidcProvider, string> = {
  google: 'Google',
  apple: 'Apple',
  dev: 'Dev',
};

export const publicConfigQuery = queryOptions({
  queryKey: qk.config,
  queryFn: () => unwrap(api.GET('/config')),
  staleTime: 5 * 60_000,
  retry: false,
});

/** Provajderi podešeni za web (redoslijed: Google, Apple, pa dev samo lokalno/e2e). */
export function useOidcProviders(): OidcProvider[] {
  const { data } = useQuery(publicConfigQuery);
  const web = new Set((data?.oidcProviders ?? []).filter((p) => p.web).map((p) => p.provider));
  return OIDC_PROVIDERS.filter((p) => web.has(p));
}

export interface OidcFlow {
  intent: OidcIntent;
  inviteToken?: string;
  returnTo?: string;
}

const FLOW_PREFIX = 'sm-oidc:';
const REGISTER_KEY = 'sm-oidc-register';

function ssGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function ssSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* privatni režim: tok radi i bez zapamćene namjere */
  }
}
function ssDel(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* */
  }
}

/** Čita (i briše) namjeru toka zapamćenu prije preusmjeravanja. */
export function takeFlow(state: string): OidcFlow | null {
  const raw = ssGet(FLOW_PREFIX + state);
  ssDel(FLOW_PREFIX + state);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OidcFlow;
  } catch {
    return null;
  }
}

/** `POST /auth/oidc/{provider}/start` pa preusmjeravanje na provajdera. */
export async function startOidc(provider: OidcProvider, flow: OidcFlow): Promise<void> {
  const res = await unwrap(
    api.POST('/auth/oidc/{provider}/start', {
      params: { path: { provider } },
      body: {
        client: 'web',
        intent: flow.intent,
        ...(flow.inviteToken ? { inviteToken: flow.inviteToken } : {}),
      },
    }),
  );
  if (!res.authorizationUrl) throw new Error('authorizationUrl missing');
  ssSet(FLOW_PREFIX + res.state, JSON.stringify(flow));
  window.location.assign(res.authorizationUrl);
}

export interface IdentityRegistration {
  ticket: string;
  email: string;
  name: string;
  provider: OidcProvider;
}

export function saveRegistration(r: IdentityRegistration): void {
  ssSet(REGISTER_KEY, JSON.stringify(r));
}

export function loadRegistration(): IdentityRegistration | null {
  const raw = ssGet(REGISTER_KEY);
  if (!raw) return null;
  try {
    const r = JSON.parse(raw) as IdentityRegistration;
    return typeof r.ticket === 'string' && typeof r.email === 'string' ? r : null;
  } catch {
    return null;
  }
}

export function clearRegistration(): void {
  ssDel(REGISTER_KEY);
}

/** Apple „Sakrij moju e-poštu“ (privatni relej). */
export const isPrivateRelay = (email?: string | null) =>
  !!email && email.toLowerCase().endsWith('@privaterelay.appleid.com');
